import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Chip, Link as MuiLink } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';
import searchService, { GlobalSearchResult } from '../services/searchService';

function useQueryParam(name: string): string {
  const { search } = useLocation();
  const params = React.useMemo(() => new URLSearchParams(search), [search]);
  return params.get(name) || '';
}

const Section: React.FC<{ title: string; count: number }>=({ title, count })=> (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 3 }}>
    <Typography variant="h6">{title}</Typography>
    <Chip size="small" label={count} />
  </Box>
);

const SearchPage: React.FC = () => {
  const q = useQueryParam('q');
  const [data, setData] = React.useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!q || q.trim().length === 0) { setData(null); return; }
      setLoading(true);
      try {
        const res = await searchService.global(q, 10);
        if (mounted) setData(res);
      } catch (e) {
        if (mounted) setData({ projects: [], offers: [], inquiries: [], orders: [], contractors: [], subcontractors: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [q]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Wyniki wyszukiwania dla: "{q}"</Typography>
      {loading && <Typography variant="body2">Ładowanie...</Typography>}
      {!loading && (!data || (
        data.projects.length + data.offers.length + data.inquiries.length + data.orders.length + data.contractors.length + data.subcontractors.length
      ) === 0) && (
        <Typography variant="body2">Brak wyników</Typography>
      )}

      {!!data && (
        <>
          <Section title="Projekty" count={data.projects.length} />
          <List dense>
            {data.projects.map(p => (
              <ListItem key={p.project_id} component={Link} to={`/projects?pid=${encodeURIComponent(p.project_id)}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${p.project_id} — ${p.project_name}`} secondary={`${p.client_name ?? ''} ${p.contract_number ? ' | ' + p.contract_number : ''}`} />
              </ListItem>
            ))}
          </List>
          <Divider />

          <Section title="Oferty" count={data.offers.length} />
          <List dense>
            {data.offers.map(o => (
              <ListItem key={o.offer_id} component={Link} to={`/offers?pid=${encodeURIComponent(o.project_id)}&oid=${encodeURIComponent(o.offer_id)}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${o.offer_id} — ${o.contractor_name ?? ''}`} secondary={`${o.package_name ?? ''} ${o.status ? ' | ' + o.status : ''}`} />
              </ListItem>
            ))}
          </List>
          <Divider />

          <Section title="Zapytania" count={data.inquiries.length} />
          <List dense>
            {data.inquiries.map(i => (
              <ListItem key={i.inquiry_id} component={Link} to={`/offers?pid=${encodeURIComponent(i.project_id)}&inq=${encodeURIComponent(i.inquiry_id)}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${i.inquiry_id} — ${i.subject ?? ''}`} secondary={`${i.project_name ?? ''} ${i.package_name ? ' | ' + i.package_name : ''}`} />
              </ListItem>
            ))}
          </List>
          <Divider />

          <Section title="Zamówienia" count={data.orders.length} />
          <List dense>
            {data.orders.map(o => (
              <ListItem key={o.id} component={Link} to={`/projects?pid=${encodeURIComponent(o.project_id)}&order=${encodeURIComponent(o.id.toString())}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${o.order_id_display ?? o.id} — ${o.contractor ?? ''}`} secondary={`${o.status ?? ''}`} />
              </ListItem>
            ))}
          </List>
          <Divider />

          <Section title="Kontrahenci" count={data.contractors.length} />
          <List dense>
            {data.contractors.map(c => (
              <ListItem key={c.id} component={Link} to={`/database?contractor=${encodeURIComponent(c.id)}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${c.name}`} secondary={`${c.nip ?? ''} ${c.email ? ' | ' + c.email : ''} ${c.location ? ' | ' + c.location : ''}`} />
              </ListItem>
            ))}
          </List>
          <Divider />

          <Section title="Podwykonawcy" count={data.subcontractors.length} />
          <List dense>
            {data.subcontractors.map(s => (
              <ListItem key={s.id} component={Link} to={`/projects?pid=${encodeURIComponent(s.project_id ?? '')}&subc=${encodeURIComponent(s.id)}`} sx={{ cursor: 'pointer' }}>
                <ListItemText primary={`${s.name}`} secondary={`${s.contract_number ?? ''} ${s.contract_subject ? ' | ' + s.contract_subject : ''}`} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default SearchPage;


